import { Action, createReducer, on, createFeatureSelector, createSelector } from '@ngrx/store';
import { UserCustomizationActions } from './../actions';
import type { CustomizationQueueItem } from '../actions/customization.actions';
import { Logo } from '@app/shared/models/logo';
import { Background } from '@app/shared/models/background';
import { DomainActions, TeamActions, SystemActions } from '@app/core/actions';
import { GetDomainCustomizationOutput } from '@smash-sdk/domain/ejs/01-2024';

export const customizationFeatureKey = 'userCustomization';

export interface State {
  customizationReceiver: GetDomainCustomizationOutput['customization'];
  customizationReceiverLoading: boolean;
  customizationReceiverError: boolean;
  customizationReceiverIsSystem: boolean;

  customizationSender: GetDomainCustomizationOutput['customization'];
  customizationSenderLoading: boolean;
  customizationSenderError: boolean;
  customizationSenderIsSystem: boolean;

  customizationToUpdate: GetDomainCustomizationOutput['customization'];
  customizationMediaLoading: boolean;
  customizationUpdating: boolean;
  customizationUpdated: boolean;
  // `string | undefined | null`, not `any`: every producer of this field is either
  // extractHttpErrorMessage/extractIamErrorMessage's return (`string | undefined`,
  // sso.effects.ts / customization.effects.ts), a plain SDK error's `.message` (domain.effects.ts,
  // also a `string`), or a reset back to `initialState.customizationUpdatingError` (`null`) --
  // matches every sibling `Observable` field on CustomizationReceiverComponent /
  // CustomizationSenderComponent, which are all strongly typed off this same `State`.
  customizationUpdatingError: string | undefined | null;


  backgrounds: Background[];
  backgroundsLoading: boolean;
  backgroundsError: any;

  // CustomizationQueueItem[] (not `any[]`, Finding 2 follow-up review): these hold the pending
  // create queue's own locally-shaped items (plain `{ id, sourceUrl, ... }`), the same shape
  // updateTeamCustomizationFailure's payload was already retyped to for D10 -- an `any[]` here
  // would let an id-less placeholder reach the exact `.id.startsWith('local_')` read in
  // customization.effects.ts through a different call site (addTeamBackgroundImageToCreateSuccess/
  // addTeamLogoToCreateSuccess below) than the one D10 closed.
  logosToCreate: CustomizationQueueItem[];
  logosToDelete: Logo[];
  backgroundsToCreate: CustomizationQueueItem[];
  backgroundsToDelete: Background[];

  logos: Logo[];
  logosLoading: boolean;
  logosError: any;

  selectedBackground: Background;
  selectedLogo: Logo;

}

export const initialState: State = {
  customizationReceiver: null,
  customizationReceiverLoading: false,
  customizationReceiverError: null,
  customizationReceiverIsSystem: false,

  customizationSender: null,
  customizationSenderLoading: false,
  customizationSenderError: null,
  customizationSenderIsSystem: false,


  customizationToUpdate: null,
  customizationMediaLoading: false,
  customizationUpdating: false,
  customizationUpdated: false,
  customizationUpdatingError: null,

  backgrounds: [],
  backgroundsLoading: false,
  backgroundsError: null,

  logosToCreate: [],
  logosToDelete: [],
  backgroundsToCreate: [],
  backgroundsToDelete: [],

  logos: [],
  logosLoading: false,
  logosError: null,

  selectedBackground: null,
  selectedLogo: null,
};

const customizationReducer = createReducer(
  initialState,

  // Load system customization
  on(SystemActions.loadManagedCustomizationSuccess, (state, { customization }) => ({
    ...state,
    customizationReceiver: customization,
    customizationToUpdate: customization,
    customizationReceiverLoading: false,
    selectedBackground: customization.background,
    selectedLogo: customization.logo,
    customizationReceiverIsSystem: true
  })),

  // Load Recipient customization
  // customizationUpdatingError is reset here too (ultra-review of PR #1611, 2/2 verifiers, MEDIUM):
  // it is a single field shared by both the receiver and sender flows and was previously cleared
  // only by a new SAVE (updateTeamCustomization/updateDomainCustomization), never by either flow's
  // page-load action -- so a failed save's error on one flow leaked onto the other flow's page as
  // soon as it loaded, before any save was attempted there.
  on(TeamActions.loadTeamCustomization, SystemActions.loadManagedCustomization, (state) => ({
    ...state,
    customizationReceiver: initialState.customizationReceiver,
    customizationReceiverLoading: true,
    customizationReceiverError: initialState.customizationReceiverError,
    customizationReceiverIsSystem: initialState.customizationReceiverIsSystem,
    customizationToUpdate: initialState.customizationToUpdate,
    customizationUpdatingError: initialState.customizationUpdatingError,
  })),
  on(TeamActions.loadTeamCustomizationSuccess, (state, { customization }) => ({
    ...state,
    customizationReceiver: customization,
    customizationReceiverLoading: false,
    selectedBackground: customization.background,
    selectedLogo: customization.logo,
  })),
  on(TeamActions.loadTeamCustomizationFailure, SystemActions.loadManagedCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationReceiver: initialState.customizationReceiver,
    customizationReceiverLoading: false,
    customizationReceiverError: error,
  })),

  // Load Team Assets logos & backgrounds (for recipient customization edit)
  on(TeamActions.loadTeamBackgrounds, (state) => ({
    ...state,
    backgrounds: initialState.backgrounds,
    backgroundsLoading: true,
    backgroundsError: initialState.backgroundsError,
  })),
  on(TeamActions.loadTeamBackgroundsSuccess, (state, { backgrounds }) => ({
    ...state,
    backgrounds,
    backgroundsLoading: false,
    backgroundsError: initialState.backgroundsError,
  })),
  // for now we dont show backgrounds in admin but need to resolve loading state
  on(SystemActions.loadManagedBackgroundsSuccess, (state) => ({
    ...state,
    backgroundsLoading: false,
    backgroundsError: initialState.backgroundsError,
  })),
  on(SystemActions.loadManagedBackgroundsFailure, (state, { error }) => ({
    ...state,
    backgroundsLoading: false,
    backgroundsError: error,
  })),
  on(TeamActions.loadTeamBackgroundsFailure, (state, { error }) => ({
    ...state,
    backgroundsLoading: false,
    backgroundsError: error,
  })),
  on(TeamActions.loadTeamLogos, (state) => ({
    ...state,
    logos: initialState.logos,
    logosLoading: true,
    logosError: initialState.logosError,
  })),
  on(TeamActions.loadTeamLogosSuccess, (state, { logos }) => ({
    ...state,
    logos,
    logosLoading: false,
    logosError: initialState.logosError,
  })),

  // for now we dont show logos in admin but need to resolve loading state
  on(SystemActions.loadManagedLogosSuccess, (state) => ({
    ...state,
    logosLoading: false,
    logosError: initialState.logosError,
  })),
  on(SystemActions.loadManagedLogosFailure, (state, { error }) => ({
    ...state,
    logosLoading: false,
    logosError: error,
  })),

  on(TeamActions.loadTeamLogosFailure, (state, { error }) => ({
    ...state,
    logosLoading: false,
    logosError: error,
  })),
  on(UserCustomizationActions.selectBackground, (state, { background }) => ({
    ...state,
    selectedBackground: background,
    customizationToUpdate: { ...state.customizationToUpdate, background },
  })),
  on(UserCustomizationActions.selectLogo, (state, { logo }) => ({
    ...state,
    selectedLogo: logo,
    customizationToUpdate: { ...state.customizationToUpdate, logo },
  })),
  on(UserCustomizationActions.addTeamBackgroundImageToCreate, (state) => ({
    ...state,
    customizationMediaLoading: true,
  })),
  on(UserCustomizationActions.addTeamBackgroundImageToCreateSuccess, (state, { background }) => ({
    ...state,
    customizationMediaLoading: false,
    // `as unknown as CustomizationQueueItem` (adversarial-review follow-up, 2026-09-17):
    // `background` is now typed as the full `Background` model (customization.actions.ts), but
    // `backgroundsToCreate` itself stays `CustomizationQueueItem[]` (D10) since that same queue
    // also holds bare id-only placeholders elsewhere (updateTeamCustomizationFailure's id-swap
    // override below). `Background.id` is optional while `CustomizationQueueItem.id` is required,
    // so the two types are not directly assignable even though the value reaching this line always
    // carries a real id (the sole producer, customization.effects.ts, always sets one).
    backgroundsToCreate: [...state.backgroundsToCreate, background as unknown as CustomizationQueueItem],
    // No cast needed: `background` is now the full `Background` model (customization.actions.ts,
    // adversarial-review follow-up 2026-09-17), matching `backgrounds: Background[]` exactly.
    backgrounds: [...state.backgrounds, background],
    customizationToUpdate: {
      ...state.customizationToUpdate,
      // `as unknown as ...` kept (adversarial-review follow-up, 2026-09-17): `background` is now
      // the full `Background` model, but this SDK output type requires `type` to be one of the
      // literal union `"Image" | "Youtube" | "Vimeo"` (Background#type is a plain `string`) and
      // requires id/sourceId/thumbnail/sourceUrl where Background leaves them optional -- the two
      // types genuinely differ structurally, so the safe alternative (no cast) does not apply here.
      background: background as unknown as GetDomainCustomizationOutput['customization']['background'],
    },
    // No cast needed: `background` is now the full `Background` model, matching
    // `selectedBackground: Background` exactly.
    selectedBackground: background,
  })),
  // Without this, a failure left customizationMediaLoading stuck at true forever (the effect died
  // uncaught before it could dispatch anything) -- see customization.effects.ts's catchError.
  on(UserCustomizationActions.addTeamBackgroundImageToCreateFailure, (state, { error }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationUpdatingError: error,
  })),
  on(UserCustomizationActions.addTeamBackgroundVideo, (state) => ({
    ...state,
    customizationMediaLoading: true,
  })),
  on(UserCustomizationActions.addTeamBackgroundVideoSuccess, (state, { background }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationToUpdate: { ...state.customizationToUpdate, background: background as GetDomainCustomizationOutput['customization']['background'] }, // FIX ME cast
    backgrounds: [...state.backgrounds, background],
    selectedBackground: background,
  })),
  on(UserCustomizationActions.addTeamBackgroundVideoFailure, (state, { error }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationUpdatingError: error,
  })),
  // Mirrors addTeamBackgroundImageToCreate above: the dispatched payload here is a placeholder for
  // an upload that has not completed yet (no real id), so it must not enter logosToCreate --
  // addTeamLogoToCreateSuccess is what appends the real, fully-formed logo once the upload
  // finishes. Pushing the raw placeholder here left a bogus id-less entry in logosToCreate
  // alongside the real one, which updateTeamCustomization$'s `.id.startsWith('local_')` check then
  // crashed on (2026-09-15 incident).
  on(UserCustomizationActions.addTeamLogoToCreate, (state) => ({
    ...state,
    customizationMediaLoading: true,
  })),
  on(UserCustomizationActions.addTeamLogoToCreateSuccess, (state, { logo }) => ({
    ...state,
    customizationMediaLoading: false,
    // `as unknown as CustomizationQueueItem`: same reasoning as
    // addTeamBackgroundImageToCreateSuccess's `backgroundsToCreate` cast above -- `logo` is now
    // the full `Logo` model (customization.actions.ts, adversarial-review follow-up 2026-09-17),
    // but `logosToCreate` stays `CustomizationQueueItem[]` (D10) since it also holds bare id-only
    // placeholders elsewhere. `Logo` has no index signature, so it is not directly assignable to
    // `CustomizationQueueItem` even though the value reaching this line is always fully-formed.
    logosToCreate: [...state.logosToCreate, logo as unknown as CustomizationQueueItem],
    // No cast needed: `logo` is now the full `Logo` model, matching `logos: Logo[]` exactly.
    logos: [...state.logos, logo],
    customizationToUpdate: {
      ...state.customizationToUpdate,
      // `as unknown as ...` kept: same reasoning as addTeamBackgroundImageToCreateSuccess's
      // `customizationToUpdate.background` cast above -- this SDK output type requires `type` to
      // be the literal `"Image"` (Logo#type is a plain `string`), so the two types genuinely
      // differ structurally.
      logo: logo as unknown as GetDomainCustomizationOutput['customization']['logo'],
    },
    // No cast needed: `logo` is now the full `Logo` model, matching `selectedLogo: Logo` exactly.
    selectedLogo: logo,
  })),
  on(UserCustomizationActions.addTeamLogoToCreateFailure, (state, { error }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationUpdatingError: error,
  })),
  on(UserCustomizationActions.addTeamBackgroundToDelete, (state, { background }) => ({
    ...state,
    backgroundsToDelete: [...state.backgroundsToDelete, background],
    backgrounds: state.backgrounds.filter(_background => background.id !== _background.id),
  })),
  on(UserCustomizationActions.addTeamLogoToDelete, (state, { logo }) => ({
    ...state,
    logosToDelete: [...state.logosToDelete, logo],
    logos: state.logos.filter(_logo => logo.id !== _logo.id),
  })),

  // Save Recipient customization
  on(UserCustomizationActions.updateTeamCustomization, (state) => ({
    ...state,
    customizationUpdating: true,
    customizationUpdated: initialState.customizationUpdated,
    customizationUpdatingError: initialState.customizationUpdatingError,
  })),
  on(UserCustomizationActions.updateCustomizationSuccess, (state) => ({
    ...state,
    customizationUpdating: false,
    customizationUpdated: true,
    logosToCreate: initialState.logosToCreate,
    backgroundsToCreate: initialState.backgroundsToCreate,
    logosToDelete: initialState.logosToDelete,
    backgroundsToDelete: initialState.backgroundsToDelete,
  })),
  on(UserCustomizationActions.updateTeamCustomizationFailure, (state, { error, createdBackgroundsSucceeded, createdLogosSucceeded, deletedBackgroundsSucceeded, deletedLogosSucceeded, backgroundsToCreate, logosToCreate, backgroundsToDelete, logosToDelete, backgroundOnFailure, logoOnFailure }) => ({
    ...state,
    customizationUpdating: false,
    customizationUpdatingError: error,
    // The action's own `backgroundsToCreate`/`logosToCreate` (see customization.actions.ts) is
    // only ever set when a create succeeded but a SIBLING create's failure pre-empted the attach
    // step -- an item that is not "fully done" (so the `Succeeded` flag below must not blank-clear
    // it) yet must not be resubmitted for creation either (its local id has already been swapped
    // for the real one). That override therefore always wins over the flag-driven clear/keep
    // below, which still governs every other failure path exactly as before.
    backgroundsToCreate: backgroundsToCreate !== undefined ? backgroundsToCreate : (createdBackgroundsSucceeded ? initialState.backgroundsToCreate : state.backgroundsToCreate),
    logosToCreate: logosToCreate !== undefined ? logosToCreate : (createdLogosSucceeded ? initialState.logosToCreate : state.logosToCreate),
    // Same reasoning as backgroundsToCreate/logosToCreate above, mirrored on the delete side (F4,
    // ultra-review finding, user-confirmed 2026-09-22): the action's own backgroundsToDelete/
    // logosToDelete override, when present, already holds exactly the items that failed to delete
    // (or were never sent, `local_`-only placeholders) -- computed by customization.effects.ts's
    // withRemainingToDelete from the per-item settled outcome -- so it always wins over the
    // flag-driven clear/keep below, which still governs every failure path where nothing was
    // queued for deletion this round.
    backgroundsToDelete: backgroundsToDelete !== undefined ? backgroundsToDelete : (deletedBackgroundsSucceeded ? initialState.backgroundsToDelete : state.backgroundsToDelete),
    logosToDelete: logosToDelete !== undefined ? logosToDelete : (deletedLogosSucceeded ? initialState.logosToDelete : state.logosToDelete),
    // Keeps customizationToUpdate.background/logo and selectedBackground/selectedLogo in
    // lock-step with the queue's own id swap above (backgroundOnFailure/logoOnFailure) --
    // customizationToUpdate.background and selectedBackground are set from the SAME object
    // reference the queue holds at first-add time (see addTeamBackgroundImageToCreateSuccess
    // above), so without this they would keep the stale local id forever, crashing (or silently
    // dropping the asset) on the very next Save (D1, docs/ultra-review/2026-09-16-sso-and-card-
    // error-investigation-delta/REPORT.md). Referentially untouched (no new object) when neither
    // override is reported, so an unrelated failure never re-renders anything bound to these.
    customizationToUpdate: backgroundOnFailure !== undefined || logoOnFailure !== undefined
      ? {
        ...state.customizationToUpdate,
        // `as unknown as ...`: backgroundOnFailure/logoOnFailure are typed CustomizationQueueItem
        // (D10) -- deliberately loose, guaranteeing only `id` -- but the actual runtime value here
        // always originates from a real background/logo object with only its `id` swapped (see
        // withRealCreatedId, customization.effects.ts), never a genuinely different shape, so the
        // safe alternative (narrowing/type-guarding) has nothing to actually check at runtime.
        ...(backgroundOnFailure !== undefined ? { background: backgroundOnFailure as unknown as GetDomainCustomizationOutput['customization']['background'] } : {}),
        // Same reasoning as background above.
        ...(logoOnFailure !== undefined ? { logo: logoOnFailure as unknown as GetDomainCustomizationOutput['customization']['logo'] } : {}),
      }
      : state.customizationToUpdate,
    // Same reasoning as the customizationToUpdate.background cast above.
    selectedBackground: backgroundOnFailure !== undefined ? (backgroundOnFailure as unknown as Background) : state.selectedBackground,
    // Same reasoning as the customizationToUpdate.logo cast above.
    selectedLogo: logoOnFailure !== undefined ? (logoOnFailure as unknown as Logo) : state.selectedLogo,
  })),


  // Load Sender customization
  // customizationUpdatingError is reset here too -- same reasoning as the receiver load handler
  // above (ultra-review of PR #1611, 2/2 verifiers, MEDIUM): without this, a failed receiver save's
  // error leaked onto the sender page as soon as it loaded.
  on(DomainActions.loadDomainCustomization, (state) => ({
    ...state,
    customizationSender: initialState.customizationSender,
    customizationSenderLoading: true,
    customizationSenderError: initialState.customizationSenderError,
    customizationSenderIsSystem: initialState.customizationSenderIsSystem,
    customizationToUpdate: initialState.customizationToUpdate,
    customizationUpdatingError: initialState.customizationUpdatingError,
  })),
  on(DomainActions.loadDomainCustomizationSuccess, (state, { customization }) => ({
    ...state,
    customizationSender: customization,
    customizationSenderLoading: false,
    selectedBackground: customization.background,
    selectedLogo: customization.logo,
  })),
  on(DomainActions.loadDomainCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationSender: initialState.customizationSender,
    customizationSenderLoading: false,
    customizationSenderError: error,
  })),

  // Add logo & background asset to domain (sender customization)
  on(DomainActions.addNewLogoToDomainCustomization, (state) => ({
    ...state,
    customizationMediaLoading: true,
  })),
  on(DomainActions.addNewLogoToDomainCustomizationSuccess, (state, { logo }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationToUpdate: {
      ...state.customizationToUpdate,
      logo: {
        ...state.customizationToUpdate.logo,
        sourceUrl: logo.downloadUrl,
        thumbnail: logo.downloadUrl,
        type: 'Image' as GetDomainCustomizationOutput['customization']['logo']['type']
      }
    },
  })),
  // Without this, a failure left customizationMediaLoading stuck at true forever (the effect died
  // uncaught before it could dispatch anything) -- see domain.effects.ts's catchError.
  on(DomainActions.addNewLogoToDomainCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationUpdatingError: error,
  })),
  on(DomainActions.addNewBackgroundImageToDomainCustomization, (state) => ({
    ...state,
    customizationMediaLoading: true,
  })),
  on(DomainActions.addNewBackgroundImageToDomainCustomizationSuccess, (state, { background }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationToUpdate: {
      ...state.customizationToUpdate,
      background: {
        ...state.customizationToUpdate.background,
        sourceUrl: background.downloadUrl,
        type: 'Image' as GetDomainCustomizationOutput['customization']['background']['type']
      }
    }
  })),
  on(DomainActions.addNewBackgroundImageToDomainCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationUpdatingError: error,
  })),
  on(DomainActions.addNewBackgroundVideoToDomainCustomization, (state) => ({
    ...state,
    customizationMediaLoading: true,
  })),
  on(DomainActions.addNewBackgroundVideoToDomainCustomizationSuccess, (state, { video }) => ({
    ...state,
    customizationMediaLoading: false,
    customizationToUpdate: {
      ...state.customizationToUpdate,
      background: {
        sourceId: video.id,
        type: video.type as GetDomainCustomizationOutput['customization']['background']['type'],
        sourceUrl: video.url
      }
    } as GetDomainCustomizationOutput['customization'], // FIX ME cast
  })),

  // Save Sender customization
  on(DomainActions.updateDomainCustomization, (state) => ({
    ...state,
    customizationUpdating: true,
    customizationUpdated: initialState.customizationUpdated,
    customizationUpdatingError: initialState.customizationUpdatingError,
  })),
  on(DomainActions.updateDomainCustomizationSuccess, (state, { customization }) => ({
    ...state,
    customizationSender: customization,
    customizationUpdating: false,
    customizationUpdated: true,
    selectedBackground: customization.background,
    selectedLogo: customization.logo,
  })),
  on(DomainActions.updateDomainCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationUpdating: false,
    customizationUpdatingError: error,
  })),

  // Set the customization to update
  on(UserCustomizationActions.setCustomizationToUpdate, (state, { target }) => ({
    ...state,
    customizationToUpdate: target === 'sender' ? { ...state.customizationSender } : { ...state.customizationReceiver }
  }))
);


export const getCustomizationState = createFeatureSelector<State>('userCustomization');

export const getCustomizationReceiver = createSelector(
  getCustomizationState,
  (state: State) => state.customizationReceiver
);

export const getCustomizationReceiverLoading = createSelector(
  getCustomizationState,
  (state: State) => state.customizationReceiverLoading
);

export const getCustomizationSender = createSelector(
  getCustomizationState,
  (state: State) => state.customizationSender
);

export const getCustomizationSenderLoading = createSelector(
  getCustomizationState,
  (state: State) => state.customizationSenderLoading
);


export const getCustomizationReceiverIsSystem = createSelector(
  getCustomizationState,
  (state: State) => state.customizationReceiverIsSystem
);

export const getTeamBackgrounds = createSelector(
  getCustomizationState,
  (state: State) => state.backgrounds
);

export const getTeamBackgroundsLoading = createSelector(
  getCustomizationState,
  (state: State) => state.backgroundsLoading
);

export const getTeamLogos = createSelector(
  getCustomizationState,
  (state: State) => state.logos
);

export const getTeamLogosLoading = createSelector(
  getCustomizationState,
  (state: State) => state.logosLoading
);

export const getSelectedBackground = createSelector(
  getCustomizationState,
  (state: State) => state.selectedBackground
);

export const getSelectedLogo = createSelector(
  getCustomizationState,
  (state: State) => state.selectedLogo
);

export const getCustomizationToUpdate = createSelector(
  getCustomizationState,
  (state: State) => state.customizationToUpdate
);

export const getCustomizationMediaLoading = createSelector(
  getCustomizationState,
  (state: State) => state.customizationMediaLoading
);

export const getCustomizationUpdating = createSelector(
  getCustomizationState,
  (state: State) => state.customizationUpdating
);

export const getCustomizationUpdated = createSelector(
  getCustomizationState,
  (state: State) => state.customizationUpdated
);

export const getCustomizationUpdatingError = createSelector(
  getCustomizationState,
  (state: State) => state.customizationUpdatingError
);

export const getLogosToCreate = createSelector(
  getCustomizationState,
  (state: State) => state.logosToCreate
);
export const getBackgroundsToCreate = createSelector(
  getCustomizationState,
  (state: State) => state.backgroundsToCreate
);
export const getLogosToDelete = createSelector(
  getCustomizationState,
  (state: State) => state.logosToDelete
);

export const getBackgroundToDelete = createSelector(
  getCustomizationState,
  (state: State) => state.backgroundsToDelete
);

export function reducer(state: State | undefined, action: Action) {
  return customizationReducer(state, action);
}


